import { useFeatureFlagEnabled } from "@posthog/react";
import { useRouterState } from "@tanstack/react-router";
import { Placeholder } from "@tiptap/extensions";
import {
	EditorContent,
	useEditor,
	type AnyExtension,
	type Editor,
	type JSONContent,
} from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import type { ChatStatus } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AdvancedModelPicker } from "@/components/advanced-model-picker";
import {
	PromptInput,
	PromptInputBody,
	PromptInputFooter,
	PromptInputHeader,
	type PromptInputMessage,
	PromptInputSubmit,
	PromptInputTools,
	usePromptInputAttachments,
} from "@/components/ai-elements/prompt-input";
import { ResourcePickerAction } from "@/components/chat-panel/resource-picker-action";
import { ModelPicker } from "@/components/model-picker";
import { ProUpgradeDialog } from "@/components/pro-upgrade-dialog";
import { PromptInputAttachmentsDisplay } from "@/components/prompt-input-attachments";
import { TextType } from "@/components/text-type";
import { useProPlan } from "@/hooks/use-pro-plan";
import { LEARNING_MATERIAL_SKILLS } from "@/lib/learning-material-skills";

import { createSkillMention, skillMentionPluginKey } from "./skill-mention";
import type { SlashMenuItem } from "./slash-menu";

const MAX_CHAT_ATTACHMENTS = 10;

type ChatInputProps = {
	handleSubmit: (message: PromptInputMessage) => void;
	handleStop?: () => void;
	status: ChatStatus;
	input: string;
	setInput: (input: string) => void;
	placeholder?: string;
	animatedPlaceholder?: string[];
	webSearch: boolean;
	setWebSearch: (enabled: boolean) => void;
	model: string;
	setModel: (model: string) => void;
};

function filterItems(items: SlashMenuItem[], query: string): SlashMenuItem[] {
	const q = query.toLowerCase().trim();
	const filtered = q
		? items.filter(
				(item) =>
					item.slug.toLowerCase().includes(q) ||
					item.name.toLowerCase().includes(q) ||
					(item.description?.toLowerCase().includes(q) ?? false),
			)
		: items;
	return filtered.slice(0, 50);
}

function getPlainText(editor: Editor) {
	if (editor.isDestroyed || !editor.state?.schema) {
		return "";
	}

	return editor.getText({ blockSeparator: "\n" }).trim();
}

function plainTextToContent(text: string): JSONContent {
	const lines = text.split(/\r?\n/);
	return {
		type: "doc",
		content: lines.map((line) => ({
			type: "paragraph",
			content: line ? [{ type: "text", text: line }] : undefined,
		})),
	};
}

function setHiddenInputValue(input: HTMLInputElement | null, value: string) {
	if (input) {
		input.value = value;
	}
}

export function ChatInput({
	handleSubmit,
	handleStop,
	status,
	input,
	setInput,
	placeholder,
	animatedPlaceholder,
	model,
	setModel,
}: ChatInputProps) {
	const isLoading = useRouterState({
		select: (state) => state.isLoading,
	});
	const advancedModelPickerEnabled = useFeatureFlagEnabled("advanced-model-picker");
	const { showUpgrade, setShowUpgrade, requirePro } = useProPlan();

	const onModelChange = (nextModel: string) => {
		if (nextModel === "pro" && !requirePro()) {
			return;
		}
		setModel(nextModel);
	};

	return (
		<>
			<PromptInput
				onSubmit={handleSubmit}
				className="mt-4"
				globalDrop
				maxFiles={MAX_CHAT_ATTACHMENTS}
				multiple
				accept="image/*,application/pdf,.doc,.docx,.txt"
			>
				<PromptInputAttachmentsHeader />
				<PromptInputBody>
					<TiptapComposerField
						input={input}
						setInput={setInput}
						placeholder={placeholder}
						animatedPlaceholder={animatedPlaceholder}
					/>
				</PromptInputBody>
				<PromptInputFooter>
					<PromptInputTools>
						<ResourcePickerAction />
						{import.meta.env.DEV || advancedModelPickerEnabled ? (
							<AdvancedModelPicker value={model} onValueChange={onModelChange} />
						) : (
							<ModelPicker value={model} onValueChange={onModelChange} />
						)}
					</PromptInputTools>
					<PromptInputSubmit
						disabled={!input.trim() || isLoading}
						status={status}
						onStop={handleStop}
					/>
				</PromptInputFooter>
			</PromptInput>
			<ProUpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} />
		</>
	);
}

function PromptInputAttachmentsHeader() {
	const attachments = usePromptInputAttachments();

	if (attachments.files.length === 0) {
		return null;
	}

	return (
		<PromptInputHeader>
			<PromptInputAttachmentsDisplay />
		</PromptInputHeader>
	);
}

type TiptapComposerFieldProps = {
	input: string;
	setInput: (input: string) => void;
	placeholder?: string;
	animatedPlaceholder?: string[];
};

function TiptapComposerField({
	input,
	setInput,
	placeholder,
	animatedPlaceholder,
}: TiptapComposerFieldProps) {
	const attachments = usePromptInputAttachments();
	const addAttachmentsRef = useRef(attachments.add);
	const formRef = useRef<HTMLFormElement | null>(null);
	const hiddenInputRef = useRef<HTMLInputElement | null>(null);
	const [isFocused, setIsFocused] = useState(false);
	const hasAnimatedPlaceholder = !!animatedPlaceholder?.length;
	const shouldShowAnimatedPlaceholder =
		!input && !isFocused && hasAnimatedPlaceholder && attachments.files.length === 0;

	const slashItems = useMemo<SlashMenuItem[]>(
		() =>
			LEARNING_MATERIAL_SKILLS.map((skill) => ({
				slug: skill.slug,
				name: skill.name,
				description: skill.description,
			})),
		[],
	);
	const slashItemsRef = useRef(slashItems);
	slashItemsRef.current = slashItems;

	const getItems = useCallback((query: string) => filterItems(slashItemsRef.current, query), []);
	const skillMention = useMemo(() => createSkillMention({ getItems }), [getItems]);

	useEffect(() => {
		addAttachmentsRef.current = attachments.add;
	}, [attachments.add]);

	const submitRef = useRef<() => void>(() => undefined);
	const editor = useEditor(
		{
			extensions: [
				StarterKit.configure({
					heading: false,
					bulletList: false,
					orderedList: false,
					blockquote: false,
					codeBlock: false,
					horizontalRule: false,
				}),
				Placeholder.configure({
					placeholder: hasAnimatedPlaceholder
						? ""
						: (placeholder ?? "可以输入任务信息或选择预置功能,输入 / 唤起技能"),
				}),
				skillMention,
			] as AnyExtension[],
			content: plainTextToContent(input),
			editorProps: {
				attributes: {
					class: "tiptap-composer min-h-16 max-h-[5.5rem] w-full flex-1 overflow-y-auto rounded-none border-0 bg-transparent px-2.5 py-2 text-base outline-none whitespace-pre-wrap break-words md:max-h-[4.75rem] md:text-sm",
					"data-slot": "input-group-control",
					"data-testid": "composer-textarea",
					"aria-label": placeholder ?? "Chat message",
				},
				handleKeyDown: (view, event) => {
					if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
						const suggestion = skillMentionPluginKey.getState(view.state) as
							| { active?: boolean }
							| undefined;
						if (suggestion?.active) return false;
						event.preventDefault();
						submitRef.current();
						return true;
					}
					return false;
				},
				handlePaste: (_view, event) => {
					const items = event.clipboardData?.items;
					if (!items) return false;

					const files: File[] = [];
					for (const item of items) {
						if (item.kind === "file") {
							const file = item.getAsFile();
							if (file) files.push(file);
						}
					}

					if (files.length === 0) return false;
					event.preventDefault();
					addAttachmentsRef.current(files);
					return true;
				},
			},
			onCreate: ({ editor }) => {
				formRef.current = editor.view.dom.closest("form") as HTMLFormElement | null;
			},
			onFocus: () => setIsFocused(true),
			onBlur: () => setIsFocused(false),
			onUpdate: ({ editor }) => {
				const text = getPlainText(editor);
				setHiddenInputValue(hiddenInputRef.current, text);
				setInput(text);
			},
			immediatelyRender: false,
		},
		[hasAnimatedPlaceholder, placeholder, setInput, skillMention],
	);

	const submit = useCallback(() => {
		if (!editor || editor.isDestroyed) return;
		const text = getPlainText(editor);
		setHiddenInputValue(hiddenInputRef.current, text);
		setInput(text);
		formRef.current?.requestSubmit();
	}, [editor, setInput]);

	submitRef.current = submit;

	useEffect(() => {
		if (!editor || editor.isDestroyed) return;
		const editorText = getPlainText(editor);
		if (input === editorText) return;
		editor.commands.setContent(plainTextToContent(input), { emitUpdate: false });
		setHiddenInputValue(hiddenInputRef.current, input);
	}, [editor, input]);

	return (
		<>
			{shouldShowAnimatedPlaceholder && (
				<TextType
					aria-hidden
					as="span"
					className="pointer-events-none absolute top-2 right-2.5 left-2.5 z-10 max-h-16 overflow-hidden text-base text-muted-foreground md:text-sm"
					text={animatedPlaceholder}
					typingSpeed={80}
					deletingSpeed={36}
					pauseDuration={3000}
					showCursor={false}
				/>
			)}
			<input ref={hiddenInputRef} type="hidden" name="message" value={input} readOnly />
			{editor ? (
				<EditorContent editor={editor} className="w-full" />
			) : (
				<TiptapComposerFallback />
			)}
		</>
	);
}

function TiptapComposerFallback() {
	return (
		<div
			aria-hidden
			data-slot="input-group-control"
			className="tiptap-composer min-h-16 max-h-[5.5rem] w-full flex-1 overflow-y-auto rounded-none border-0 bg-transparent px-2.5 py-2 text-base outline-none whitespace-pre-wrap break-words md:max-h-[4.75rem] md:text-sm"
		/>
	);
}
